import { type ReactNode } from 'react';
import { keyframes } from '@uhg-abyss/web/tools/styled';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import type { ModalDialogProps } from '@uhg-abyss/web/ui/ModalDialog';

type DrawerSize = 'sm' | 'md' | 'fullscreen' | string | number;

interface BottomSheetDialogProps extends Omit<ModalDialogProps, 'children' | 'css'> {
    children: ReactNode;
    size?: DrawerSize;
    fromBottom?: boolean;
    disableAnimation?: boolean;
    maxHeight?: string;
    maxWidth?: string;
    borderRadius?: string;
    contentContainerCss?: Record<string, unknown>;
    css?: ModalDialogProps['css'];
}

const slideInFromBottom = keyframes({
    from: { transform: 'translateY(100%)' },
    to: { transform: 'translateY(0)' },
});

export function BottomSheetDialog({
    isOpen,
    onClose,
    children,
    ariaLabel = 'Drawer',
    size = 'md',
    header,
    footer,
    hideClose = false,
    alertDialog = false,
    closeOnClickOutside = true,
    fromBottom = true,
    disableAnimation = false,
    maxHeight = '90vh',
    maxWidth = '100%',
    borderRadius = '1rem 1rem 0 0',
    contentContainerCss,
    css,
    ...modalDialogProps
}: BottomSheetDialogProps) {
    const bottomSheetCss = fromBottom
        ? {
            position: 'fixed',
            top: 'auto',
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: 'auto',
            maxHeight,
            maxWidth,
            borderRadius,
            overflowY: 'auto',
            margin: 0,
            ...(!disableAnimation && {
                '@media (prefers-reduced-motion: no-preference)': {
                    '&[data-state="open"]': {
                        animation: `${slideInFromBottom} 150ms linear forwards`,
                    },
                },
            }),
        }
        : {};

    const externalCss = (css ?? {}) as Record<string, unknown>;
    const externalContentContainerCss =
        (externalCss['abyss-modal-base-content-container'] as Record<string, unknown> | undefined) ??
        {};

    return (
        <ModalDialog
            {...modalDialogProps}
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={ariaLabel}
            size={size}
            header={header}
            footer={footer}
            hideClose={hideClose}
            alertDialog={alertDialog}
            closeOnClickOutside={closeOnClickOutside}
            css={{
                ...externalCss,
                'abyss-modal-base-content-container': {
                    ...bottomSheetCss,
                    ...externalContentContainerCss,
                    ...contentContainerCss,
                },
            }}
        >
            {children}
        </ModalDialog>
    );
}
